/**
 * Deferred Surfaces — central flag registry (GH #160–#168).
 *
 * OMAR DECISION: these nine UI surfaces are deferred / not production-ready
 * and are hidden from the app as a REVERSIBLE, flag-gated hide — no code is
 * deleted. Flipping a flag to `true` restores that surface with no other
 * code changes required; every gated entry point is listed below and in the
 * source file that consumes the flag.
 *
 * A flag's comment lists every gate CURRENTLY KNOWN to consume it. That list
 * was NOT complete when this file was written: #164 and #165 hid the Policy
 * Management and Risk Register routes but left the DPO console still
 * rendering their cards, task buckets and the "Top risk score" KPI, all
 * deep-linking to routes that no longer exist. Omar ruled on 2026-08-24 to
 * hide those too; the entries below now include them. Treat the lists as a
 * maintained inventory, not a proof of completeness — when you add a surface
 * that references a deferred module, gate it AND add it here.
 *
 * BACKEND HALF: two of these flags are mirrored in Python at
 * `backend/modules/dpo/cross_module/partners/__init__.py`
 * (`DEFERRED_MODULE_IDS`), because the DPO console's cards / task buckets /
 * risk KPI are built server-side and a JS flag cannot reach them. There is
 * no shared flag store across that boundary, so SHOW_POLICY_MANAGEMENT and
 * SHOW_RISK_REGISTER must be flipped in BOTH files together.
 *
 * Do NOT delete the gated branches when a flag is `false` — that defeats the
 * reversibility this file exists to guarantee.
 */
const DEFERRED_SURFACES = Object.freeze({
  // #160 — "Module Health" tab (Admin > System Health) + the entire
  // Dashboard Module Health widget (not just its "View all" CTA — the
  // widget is dropped from DashboardPage's widget registry, so it never
  // renders in the grid or in the WidgetCustomizer's available-widgets
  // list either). Flip true to restore: SystemHealthPage.js TABS 'health'
  // entry + render branch, DashboardPage.js allNonBreachWidgets' module_health
  // filter, and ModuleHealthWidget.js's navigate-to-tab button.
  SHOW_MODULE_HEALTH: false,

  // #161 — Review Center Settings "Alerts & Channels" tab (email/Slack
  // notification channel drivers are not yet built). Flip true to restore:
  // SettingsLayout.js SECTIONS "Notifications" group + PANELS.alerts entry.
  SHOW_ALERT_CHANNELS_TAB: false,

  // #162 — Data Flow > Compliance > "Regulatory" sub-tab (SAMA banking
  // approval + NCA DCC assessment forms). Flip true to restore:
  // ComplianceSection.js showRegulatory gate (tab entry + render branch).
  SHOW_DATAFLOW_REGULATORY_TAB: false,

  // #163 — Data Flow > Governance > "Data Processors" sub-tab (processor
  // chain visualization + breach-cascade simulation). Flip true to restore:
  // GovernanceSection.js processors tab entry + render branch.
  SHOW_DATA_PROCESSORS_TAB: false,

  // #164 — Policy Management module (Sidebar nav item + App.js main route +
  // 5 legacy redirect routes) AND every DPO-console surface that points at
  // it (Omar 2026-08-24): the Command Center "Policy Management" card, the
  // Tasks-tab POLICY accordion, and the POLICY option in the task module
  // filter — all of which deep-linked to /policy-management, which is not
  // routed while this flag is false. Flip true to restore:
  //   Sidebar.js SIDEBAR nav entry
  //   App.js route + 5 legacy redirects
  //   CommandCenterTab.js SECTIONS.oversight modules
  //   DPOTasksTab.js MODULE_ORDER
  //   TaskFilters.js MODULE_KEYS
  //   backend partners/__init__.py DEFERRED_MODULE_IDS  ← must flip together
  SHOW_POLICY_MANAGEMENT: false,

  // #165 — Risk Register module (Sidebar nav item + App.js route + the
  // Dashboard ComplianceGapWidget "risk" gap-ring deep link) AND every
  // DPO-console surface that points at it (Omar 2026-08-24): the Command
  // Center "Risk Register" card, the Tasks-tab RISK accordion, the RISK
  // option in the task module filter, and the "Top risk score" KPI tile —
  // all of which read a module with no route while this flag is false.
  // Flip true to restore:
  //   Sidebar.js SIDEBAR nav entry
  //   App.js /risk route
  //   ComplianceGapWidget.js DIMENSION_ROUTES.risk
  //   CommandCenterTab.js SECTIONS.posture modules
  //   DPOTasksTab.js MODULE_ORDER
  //   TaskFilters.js MODULE_KEYS
  //   DPOKPIRibbon.js CELL_DEFINITIONS topRiskScore cell
  //   backend partners/__init__.py DEFERRED_MODULE_IDS  ← must flip together
  //   (which also restores ribbon_service's topRiskScore cell)
  SHOW_RISK_REGISTER: false,

  // #166 — Breach Notifications module (Sidebar nav item + App.js route +
  // the notification-bell slide-over's "View all" footer link). Flip true
  // to restore Sidebar.js + App.js + NotificationsPanel.js.
  SHOW_BREACH_NOTIFICATIONS: false,

  // #167 — Reports module (Sidebar nav item + App.js route). Flip true to
  // restore both entries in Sidebar.js + App.js.
  SHOW_REPORTS: false,

  // #168 — Settings page kebab menu (Approver Configuration / Compliance
  // Cycle / Identity Providers admin links). The underlying routes stay
  // reachable by direct URL regardless of this flag — only the menu entries
  // are gated. Flip true to restore SettingsPage.js relatedPages array.
  SHOW_SETTINGS_KEBAB_LINKS: false,
});

export default DEFERRED_SURFACES;
